/**
 * "just now", "5m ago", "2h ago", "Yesterday", "Sep 18" — a timestamp as a
 * person reads it.
 *
 * Lifted out of MessagesListDrawer so the feed's comments read the same way a
 * message does. They are the same kind of thing: something someone wrote a
 * moment ago, where the useful information is "recently", not a date.
 *
 * WHY toUtc IS STILL HERE
 * ----------------------
 * Most timestamps in this app come from timezone-NAIVE columns written with
 * `datetime.now(timezone.utc)` — the instant is UTC but the marker is stripped,
 * so `new Date(...)` reads it as LOCAL time and a thing posted a second ago
 * renders as hours old, off by exactly the reader's offset. The MindShare
 * schemas now attach the offset server-side, but most endpoints still do not,
 * so this normalises defensively. It is a no-op on a value that already
 * carries one.
 */

import { formatDistanceToNow, parseISO } from "date-fns";
import { formatDisplayDate } from "@/utils/formatDisplayDate";

// Backend timestamps have no timezone suffix — normalize to UTC before parsing
const toUtc = (s: string) => (/[Zz]|[+-]\d{2}:\d{2}$/.test(s) ? s : s + "Z");

export function formatRelativeTime(dateStr: string | null | undefined): string | null {
  if (!dateStr) return null;
  try {
    const date = parseISO(toUtc(dateStr));
    const now = new Date();
    const diffHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
    if (diffHours < 24) {
      return formatDistanceToNow(date, { addSuffix: true })
        .replace("less than a minute ago", "just now")
        .replace("about ", "")
        .replace(" minutes ago", "m ago")
        .replace(" minute ago", "m ago")
        .replace(" hours ago", "h ago")
        .replace(" hour ago", "h ago")
        .replace(" seconds ago", "s ago")
        .replace(" second ago", "s ago");
    }
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    if (
      date.getDate() === yesterday.getDate() &&
      date.getMonth() === yesterday.getMonth() &&
      date.getFullYear() === yesterday.getFullYear()
    ) {
      return "Yesterday";
    }
    // Older than yesterday: the standard date, e.g. "Nov 6, 2026".
    return formatDisplayDate(date, { variant: "date" });
  } catch {
    return null;
  }
}
